import { cn } from "@/common/utils/css.utils";
import {
  type Facet,
  isChoiceFacet,
  isTermsFacet,
} from "@/features/search/api/types";
import { ChoiceFacet } from "@/features/search/components/facets/ChoiceFacet";
import { FacetSection } from "@/features/search/components/facets/FacetSection";
import { RangeFacet } from "@/features/search/components/facets/RangeFacet";
import { TermsFacet } from "@/features/search/components/facets/TermsFacet";
import { CategoryTaxonomy } from "@/features/search/components/taxonomy/CategoryTaxonomy";
import { useSearchContext } from "@/features/search/context/SearchContext";
import { useFacetLabel } from "@/features/search/hooks/useFacetLabel";
import { useTaxonomyRows } from "@/features/search/hooks/useTaxonomyRows";
import {
  getFacetSelectedCount,
  getVisibleFacets,
  hasAnySelection,
} from "@/features/search/utils/facets.utils";

/* Purely on `type` — which facet is which, and how each one reads, is the service's call. */
const renderFacet = (facet: Facet) => {
  if (isTermsFacet(facet)) {
    return <TermsFacet facet={facet} />;
  }

  return isChoiceFacet(facet) ? (
    <ChoiceFacet facet={facet} />
  ) : (
    <RangeFacet facet={facet} />
  );
};

/**
 * Renders every visible facet from the response, dispatching on `type`. New
 * facets from the API appear here automatically — no code change needed.
 *
 * The category taxonomy sits above them, outside the fieldset: it is navigation, not a
 * form control, and `disabled` on a fieldset does not — and should not — disable links.
 * When it has rows it replaces the flat Category checkboxes, which say less (a hundred
 * values, no hierarchy) and can't widen. When it doesn't, they stay — better a flat list
 * than no category control at all.
 *
 * The `category` facet itself is still needed either way: the taxonomy takes its counts
 * from it, and `ActiveFilters` still offers a chip for a category the user picked.
 */
export const FacetPanel = () => {
  const { query } = useSearchContext();
  const facetLabel = useFacetLabel();
  const taxonomyRows = useTaxonomyRows();
  const facets = getVisibleFacets(query.data?.facets ?? []).filter(
    facet => !(taxonomyRows && facet.key === "category")
  );

  return (
    <>
      <CategoryTaxonomy />

      {/* `disabled` on the fieldset natively disables every control inside while a
          new result set is loading; `isPlaceholderData` is true only then. */}
      {facets.length > 0 && (
        <fieldset
          disabled={query.isPlaceholderData}
          className={cn(
            "m-0 flex min-w-0 flex-col border-0 p-0 transition-opacity",
            query.isPlaceholderData && "opacity-60"
          )}
        >
          {facets.map(facet => (
            <FacetSection
              key={facet.key}
              title={facetLabel(facet.key)}
              filterId={facet.key}
              selectedCount={getFacetSelectedCount(facet)}
              /* The badge counts refinements; starting open is about whether there is anything
                 in here to see, which a locked filter is — /sale's discount floor lives in a
                 section that would otherwise open onto nothing the page had explained. */
              defaultOpen={hasAnySelection(facet)}
            >
              {renderFacet(facet)}
            </FacetSection>
          ))}
        </fieldset>
      )}
    </>
  );
};
